import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState({
    accessToken: localStorage.getItem('accessToken'),
    refreshToken: localStorage.getItem('refreshToken')
  });
  const isLoginInProgress = useRef(false);

  const fetchUser = useCallback(async (token) => {
    const accessToken = token || tokens.accessToken;
    if (!accessToken) return;

    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      // Only logout on auth errors, not network errors
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [tokens.accessToken]);

  useEffect(() => {
    // Skip fetchUser if login is in progress (we'll fetch after login completes)
    if (isLoginInProgress.current) {
      return;
    }

    if (tokens.accessToken) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [tokens.accessToken, fetchUser]);

  const login = async (email, password) => {
    isLoginInProgress.current = true;

    const response = await axios.post(`${API_URL}/auth/login`, {
      email,
      password
    });

    const { access_token, refresh_token, user: userData } = response.data;
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);

    // If backend returns user data, use it directly (no extra API call needed)
    if (userData) {
      setUser(userData);
      setLoading(false);
    }

    setTokens({ accessToken: access_token, refreshToken: refresh_token });
    isLoginInProgress.current = false;

    // Fetch user in background if not provided by login response
    if (!userData) {
      fetchUser(access_token);
    }

    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API_URL}/auth/register`, userData);
    
    const { access_token, refresh_token } = response.data;
    localStorage.setItem('accessToken', access_token);
    localStorage.setItem('refreshToken', refresh_token);
    setTokens({ accessToken: access_token, refreshToken: refresh_token });
    
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setTokens({ accessToken: null, refreshToken: null });
    setUser(null);
  };

  const value = {
    user,
    loading,
    tokens,
    login,
    register,
    logout,
    fetchUser,
    isAuthenticated: !!tokens.accessToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
